/**
 * @file AddressAutocomplete.tsx
 * @brief Street address field with Geoapify search sheet (backend proxy)
 *
 * @details
 * WEBPANEL equivalent of mobile `VZAddressAutocomplete`: Search opens a
 * `PickerSheet` (async controlled mode), hint under the control (no fill),
 * then an editable street Input. Selecting a suggestion fills street and
 * returns structured parts so the parent can set city / state / postal /
 * country (company forms store country *name* via `findCountry`).
 *
 * @dependencies
 * - features/address/api
 * - components/ui/{Input, Label, Button, PickerSheet}
 * - shared/data/countries
 *
 * @references
 * - FRONTEND/.../VZAddressAutocomplete.tsx
 * - BACKEND/src/features/address/routes.js
 *
 * @author DES
 * @date 27-07-2026
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { MapPin, Search } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { BARE_FIELDS } from "@/components/ui/fieldStyle";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { fetchAddressSuggestions, type AddressSuggestion } from "@/features/address/api";
import { findCountry } from "@/shared/data/countries";
import { cn } from "@/shared/theme/cn";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------

export type AddressFillParts = {
  address: string;
  city: string;
  state: string;
  postal_code: string;
  /** Country *name* for company columns / CountryPicker (not ISO code) */
  country: string;
};

type Props = {
  id?: string;
  label?: string;
  value: string;
  onChange: (street: string) => void;
  /** Called when a suggestion is picked — parent updates city/state/etc. */
  onSelect: (parts: AddressFillParts) => void;
  disabled?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
  /** Leading icon for the bare field (see FormFieldGroup). */
  icon?: ReactNode;
};

type SuggestionItem = PickerSheetItem & { suggestion: AddressSuggestion };

const DEBOUNCE_MS = 350;
const MIN_CHARS = 3;

const DEFAULT_HINT =
  "Search first for best results, e.g. 15 Birchfield Drive or Flat 3, High Street";

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function toFillParts(s: AddressSuggestion): AddressFillParts {
  const country = findCountry(s.countryCode)?.name ?? (s.countryCode ? s.countryCode : "");
  return {
    address: s.address || s.label,
    city: s.city || "",
    state: s.state || "",
    postal_code: s.postalCode || "",
    country,
  };
}

function suggestionSubtitle(s: AddressSuggestion): string {
  return [s.city, s.postalCode, s.countryCode]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean)
    .join(" · ");
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------

export function AddressAutocomplete({
  id = "address-street",
  label = "Street",
  value,
  onChange,
  onSelect,
  disabled,
  placeholder = "221B Baker Street",
  hint = DEFAULT_HINT,
  icon,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<SuggestionItem[]>([]);
  const [loading, setLoading] = useState(false);

  const openSheet = useCallback(() => {
    setQuery(value?.trim() || "");
    setItems([]);
    setOpen(true);
  }, [value]);

  useEffect(() => {
    if (!open) return;

    const q = query.trim();
    if (q.length < MIN_CHARS) {
      setItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const rows = await fetchAddressSuggestions(q, { limit: 10 });
        if (cancelled) return;
        setItems(
          rows.map((s) => ({
            id: s.id,
            label: s.address || s.label.split(",")[0]?.trim() || s.label,
            subtitle: suggestionSubtitle(s) || s.label,
            suggestion: s,
          })),
        );
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, open]);

  useEffect(() => {
    if (open) return;
    setLoading(false);
    setItems([]);
  }, [open]);

  const emptyText = useMemo(() => {
    const q = query.trim();
    if (q.length > 0 && q.length < MIN_CHARS) {
      return `Type at least ${MIN_CHARS} characters to search`;
    }
    if (q.length >= MIN_CHARS && !loading) {
      return "No matches — try a different spelling";
    }
    return "Try 15 Birchfield Drive or Flat 3, High Street";
  }, [query, loading]);

  const picker = (
    <PickerSheet<SuggestionItem>
      anchor={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          disabled={disabled}
        >
          <Search className="h-3.5 w-3.5" />
          Search
        </Button>
      }
      menuAlign="end"
      open={open}
      onOpenChange={(next) => (next ? openSheet() : setOpen(false))}
      title="Search address"
      subtitle="Worldwide address lookup"
      searchPlaceholder="e.g. 15 Birchfield Drive or Flat 3"
      items={items}
      searchValue={query}
      onSearchChange={setQuery}
      loading={loading}
      clearable={false}
      emptyText={emptyText}
      onSelect={(item) => {
        const parts = toFillParts(item.suggestion);
        onChange(parts.address);
        onSelect(parts);
        setOpen(false);
      }}
      renderLeftAccessory={() => (
        <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <MapPin className="h-5 w-5" />
        </span>
      )}
    />
  );

  // The app's bare field (fieldStyle.tsx, 01-10-2026): the label is the
  // caption / placeholder and Search sits at the end of the value line.
  if (BARE_FIELDS && label) {
    return (
      <div className={className}>
        <FormFieldGroup label={label} htmlFor={id} hint={hint} icon={icon}>
          <div className="flex items-center gap-2">
            <Input
              id={id}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1"
            />
            {picker}
          </div>
        </FormFieldGroup>
      </div>
    );
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-end justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {picker}
      </div>

      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />

      {hint ? <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p> : null}
    </div>
  );
}
